import { useSelector } from 'react-redux';
import cx from 'classnames';

// Types
import { ConfigRow, OrderTotalsProps } from './OrderTotals.types';

// Config
import { orderTotalsConfirmationPagePropsState, orderTotalsOrderFormState } from './OrderTotals.state';
import { isZeroAmount } from '@utils/index';

// Components
import BodyText from '@components/body-text';
import Container from '@components/container';
import Heading from '@components/heading';
import LoadingWrapper from '@components/loading-wrapper';

// Styles
import styles from './order-totals.scss';

const formatCurrencyValue = (value?: number | string) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const stringValue = String(value).replace(/[^\d.-]/g, '');
  const numericValue = Number(stringValue);

  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const OrderTotals = ({
  confirmationPage = false,
  containerWide = false,
  editBasketModal = false,
  isDeliveryPage = false,
  sidebarView = false,
}: OrderTotalsProps) => {
  const {
    amounts,
    applyRewardsApiCallActive,
    isGiftReceiptOrder,
    isClickCollectOrder,
    outstandingBalanceExcludingDeliveryChargeRaw,
  } = useSelector(confirmationPage ? orderTotalsConfirmationPagePropsState : orderTotalsOrderFormState);

  const {
    deliveryCharge = '',
    grandTotal,
    itemTotal,
    outstandingBalance,
    partnerDiscountTotal,
    paymentTotals: { giftCard = '', giftVoucher = '' } = {},
    rewardsDiscountTotal,
    itemsDiscountTotal,
  } = amounts ?? {};

  const hasPromotionalCodeDiscount = !isZeroAmount(itemsDiscountTotal);
  const showGiftCard = !confirmationPage && !isZeroAmount(giftCard);
  const showGiftVoucher = !confirmationPage && !isZeroAmount(giftVoucher);
  const hasDeliveryCharge = deliveryCharge && !isDeliveryPage;
  const outstandingBalanceForDisplay =
    isDeliveryPage && outstandingBalanceExcludingDeliveryChargeRaw
      ? formatCurrencyValue(outstandingBalanceExcludingDeliveryChargeRaw)
      : outstandingBalance;

  const config: ConfigRow[] = [
    {
      condition: true,
      testId: 'order-totals-basket-total',
      content: [
        { component: BodyText, text: 'Basket subtotal', type: 'l' },
        { component: BodyText, text: itemTotal, type: 'l' },
      ],
    },
    {
      condition: partnerDiscountTotal,
      testId: 'order-totals-partner-discount-total',
      content: [{ text: 'Partner discount' }, { text: `-${partnerDiscountTotal}` }],
    },
    {
      condition: hasPromotionalCodeDiscount,
      testId: 'order-totals-promotional-code',
      content: [{ text: 'Promo code' }, { text: `-${itemsDiscountTotal}` }],
    },
    {
      condition: rewardsDiscountTotal,
      testId: 'order-totals-reward',
      content: [
        { text: 'Reward applied' },
        {
          loadingClassName: styles.rewardsDiscountTotalLoading,
          text: `-${rewardsDiscountTotal}`,
          isLoading: applyRewardsApiCallActive,
        },
      ],
    },
    {
      condition: hasDeliveryCharge,
      testId: 'order-totals-delivery',
      content: [{ text: isClickCollectOrder ? 'Collection charge' : 'Delivery charge' }, { text: `${deliveryCharge}` }],
    },
    {
      condition: hasDeliveryCharge && (showGiftCard || showGiftVoucher),
      testId: 'order-totals-subtotal',
      content: [{ text: 'Total' }, { text: `${grandTotal}` }],
    },
    {
      condition: showGiftCard,
      testId: 'order-totals-gift-card',
      content: [{ text: 'Gift cards applied' }, { text: `-${giftCard}` }],
    },
    {
      condition: showGiftVoucher,
      testId: 'order-totals-gift-voucher',
      content: [{ text: 'Gift vouchers applied' }, { text: `-${giftVoucher}` }],
    },
    {
      className: styles.outstandingBalance,
      condition: true,
      testId: 'order-totals-balance',
      content: [
        {
          component: Heading,
          subtitle: true,
          text: confirmationPage ? 'Order total paid' : 'Order total',
          type: 'm',
        },
        { component: Heading, text: confirmationPage ? grandTotal : outstandingBalanceForDisplay, type: 's' },
      ],
    },
    {
      className: '-mt-0_5',
      condition: !deliveryCharge || isDeliveryPage,
      testId: 'order-totals-delivery-charge-info',
      content: [{ text: '(excluding delivery)' }],
    },
    {
      className: cx(styles.giftMessagePricesInfo, {
        [styles.sidebar]: sidebarView,
      }),
      condition: isGiftReceiptOrder,
      testId: 'gift-message-prices-info',
      content: [{ tag: 'p', text: 'Prices will not be shown on the gift receipt' }],
    },
  ];

  return (
    <Container
      className={cx(styles.container, {
        [styles.isDeliveryPage]: isDeliveryPage,
        [styles.sidebar]: sidebarView,
        [styles.containerWide]: containerWide,
        [styles.editBasketModal]: editBasketModal,
      })}
      flex={{ flexDirection: 'col', gap: '1' }}
      tag="section"
      testId="order-totals-container"
    >
      {config.map(
        ({ className, condition, testId, content }) =>
          condition && (
            <Container key={testId} className={className} flex={{ justifyContent: 'space-between' }} testId={testId}>
              {content.map(
                (
                  {
                    component: Component = BodyText,
                    isLoading,
                    loadingClassName,
                    subtitle = false,
                    tag = 'span',
                    text,
                    type,
                  },
                  index,
                ) => {
                  const key = `${testId}_${index}`;

                  return (
                    <Component key={key} tag={tag} type={type} {...(Component === Heading ? { subtitle } : {})}>
                      <LoadingWrapper className={loadingClassName} isLoading={isLoading}>
                        {text}
                      </LoadingWrapper>
                    </Component>
                  );
                },
              )}
            </Container>
          ),
      )}
    </Container>
  );
};

export default OrderTotals;
